import { observer, useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './app.module.scss';
import { AppStore } from './app.store';
import { RadialBackgroundType, RadialGradient } from './components/background/radial-gradient';
import { Header } from './components/header/header';
import { BasicLoader } from './components/loaders/basic-loader';
import { AppContextProvider } from './core/context/app.context';
import { AnalyticsJourneyKey } from './core/enums/analytics-journey-key';
import { BaseRoute } from './core/models/base-routes';
import { Router } from './core/router/router';
import ScrollToTop from './core/router/scroll-to-top';
import { AnalyticsStore } from './core/stores/analytics.store';
import { Util } from './utils/common.utils';
import { StorageHelper } from './core/services/storage';
import { CacheAreaKey } from './core/config/cache-config';
import { HaptikStore } from './core/stores/haptick.store';
import { HaptikContextProvider } from './core/context/haptik.context';
import { environment } from '../environments/environment';
import appConfig from './core/services/config.service';

const App = observer<{ isLoading: boolean }>(() => {
  const appStore = useLocalObservable(() => new AppStore());
  const haptikStore = useLocalObservable(() => new HaptikStore());
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const matchRegisterPath = useMatch('/register/:packageCode');
  const matchConfirmPath = useMatch('/confirm-package/:packageCode');

  //Check query params to see if we need to change allowed payment methods
  useEffect(() => {
    if (searchParams.get('isDiscovery')) {
      appStore.disableMonthToMonthPayments();
      appStore.changeDiscoveryPaymentState(true);
    }

    setCurrentTheme();

    capitecCustomerCheck();
  }, [searchParams, matchConfirmPath]);

  // NOTE: initialize Guest Haptik bot
  useEffect(() => {
    if (environment.SHOW_HAPTIK_ON_BUYING_JOURNEY) {
      haptikStore.onInit();
    }
  }, [haptikStore]);

  // NOTE: Attempts to sign in Haptik bot
  useEffect(() => {
    if (appStore.isAuthenticated && haptikStore.canSignIn && environment.SHOW_HAPTIK_ON_BUYING_JOURNEY) {
      haptikStore.loadSignInBot();
    }
  }, [haptikStore, haptikStore.canSignIn, appStore.isAuthenticated]);

  //NOTE:Start analytics journey
  useEffect(() => {
    AnalyticsStore.startJourney(AnalyticsJourneyKey.AppLoad);
  }, []);

  // NOTE: Updates page title
  useEffect(() => {
    appStore.changeTitle(appStore.activeRoute);
  }, [appStore, appStore.activeRoute]);

  // NOTE: Gets selected package
  useEffect(() => {
    if (matchRegisterPath?.params?.packageCode || matchConfirmPath?.params?.packageCode) {
      appStore.changeSelectedPackage(
        `${matchRegisterPath?.params?.packageCode || matchConfirmPath?.params?.packageCode}`
      );
    }
  }, [appStore, matchRegisterPath, matchConfirmPath, location]);

  // NOTE: Handles login & App init
  useEffect(() => {
    const connectStore = localStorage.getItem('connect-store');
    if (Util.CheckUrlForConnectHash(window.location.href)) {
      const { access_token, expires_in, id_token, token_type, expires_time } = Util.ParseConnectUrlHash(
        window.location.href
      );
      appStore.initialise({
        connectStore: {
          accessToken: access_token,
          expiresIn: expires_in,
          idToken: id_token,
          tokenType: token_type,
          expiresTime: expires_time,
        },
      });
      return;
    } else if (connectStore && !StorageHelper.GetInstance.getFromCache('conf', 'connect', CacheAreaKey.Customer)) {
      try {
        const { access_token, expires_in, id_token, token_type, expires_time } = JSON.parse(connectStore);
        appStore.initialise({
          connectStore: {
            accessToken: access_token,
            expiresIn: expires_in,
            idToken: id_token,
            tokenType: token_type,
            expiresTime: expires_time,
          },
        });
      } catch (e) {
        console.error('Failed parsing connect store');
        localStorage.removeItem('connect-store');
        appStore.initialise();
      }
    } else {
      appStore.initialise();
    }
  }, [appStore, searchParams, navigate, location, params]);

  const handleRouteLoad = (route: BaseRoute) => {
    appStore.changeActiveRoute(route.name);
  };

  const capitecCustomerCheck = () => {
    if (matchConfirmPath) {
      if (searchParams.get('isCapitec')) {
        appStore.changeCapitecState(searchParams.get('isCapitec') ?? 'false');
        return;
      }

      appStore.changeCapitecState('false');
    }
  };

  const setCurrentTheme = () => {
    if (searchParams.get('theme')) {
      appStore.changeSelectedTheme(searchParams.get('theme') ?? 'dark');
      return;
    }

    appStore.changeCapitecState('dark');
  };

  return (
    <>
      <Helmet>
        <title>{appStore.title}</title>
        <body className="dstv-via-streaming" data-theme={appStore.activeTheme}></body>
      </Helmet>

      <BasicLoader isLoading={appStore.isLoading} fullHeight={true} topOffset={true}>
        <>
          {appStore.activeRoute && appStore.activeRoute.title !== 'Invalid Page' ? (
            <Header
              activeStep={appStore.activeStep}
              navigate={appStore.navigate}
              headerTitle={appStore.headerTitle}
              headerConfig={appConfig().getConfig(appStore.activeRoute)}
              backgroundType={RadialBackgroundType.Primary}
            />
          ) : null}
          <RadialGradient backgroundType={RadialBackgroundType.Primary} />
          <AppContextProvider appStore={appStore}>
            <HaptikContextProvider haptikStore={haptikStore}>
              <div className="app-container">
                <div className={styles.mainContent}>
                  <main className={styles.main}>
                    <ScrollToTop />
                    <Router routes={appStore.routes?.availableRoutes} onLoad={handleRouteLoad}></Router>
                  </main>
                </div>
              </div>
            </HaptikContextProvider>
          </AppContextProvider>
        </>
      </BasicLoader>
    </>
  );
});

export default App;
